import { useCallback } from "react";
import { useCachedQuery } from "./useCachedQuery";
import { fetchTenantPluginUsage, type TenantPluginUsage } from "./useAdminStatus";
import { instancePluginUsageResource } from "../lib/adminQueries";
import { rangeToWindowHours, type InstanceRange } from "../lib/instanceDetailRange";

export type InstancePluginUsageResult = {
  status: "loading" | "ready" | "error";
  plugins: ReadonlyArray<TenantPluginUsage>;
  error: string | null;
  isFetching: boolean;
};

export const useInstancePluginUsage = (tenantId: string, range: InstanceRange): InstancePluginUsageResult => {
  const hours = rangeToWindowHours(range);
  const fetcher = useCallback(
    (signal: AbortSignal): Promise<ReadonlyArray<TenantPluginUsage>> =>
      fetchTenantPluginUsage(tenantId, hours, signal),
    [tenantId, hours],
  );
  const { status, data, error, isFetching } = useCachedQuery<ReadonlyArray<TenantPluginUsage>>({
    // Cache only the default 24h window, like the metrics hook: a 30d payload
    // painting as the 24h table on a cold cache would misreport the numbers.
    cacheResource: range === "24h" ? instancePluginUsageResource(tenantId) : null,
    queryKey: `/api/admin/claws/${tenantId}/plugin-usage?hours=${hours}`,
    fetcher,
  });
  return { status, plugins: data ?? [], error, isFetching };
};
